<template>
  <div class="kt-portlet kt-portlet--mobile">
    <div class="kt-portlet__head">
      <div class="kt-portlet__head-label">
        <h3 class="kt-portlet__head-title">
          Clear PM Image — Retention Job
          <small class="text-muted ml-2">จำลอง cronjob · /Api/PMImageRetention</small>
        </h3>
      </div>
      <div class="kt-portlet__head-toolbar">
        <button class="btn btn-secondary btn-sm mr-2" @click="$router.push('/Monitoring/ScheduledJobs')">
          <i class="fas fa-arrow-left"></i> กลับ
        </button>
        <button class="btn btn-primary btn-sm" @click="loadStatus" :disabled="isLoading">
          <i class="fas fa-sync-alt"></i> {{ isLoading ? "กำลังโหลด..." : "Refresh" }}
        </button>
      </div>
    </div>

    <div class="kt-portlet__body">
      <!-- ================= คำเตือน ================= -->
      <div class="alert alert-warning">
        <strong><i class="fas fa-exclamation-triangle"></i> อ่านก่อนกด</strong>
        <ul class="mb-0 mt-2">
          <li><b>Preview</b> — dry-run นับอย่างเดียว ปลอดภัย 100% กดได้ตลอด</li>
          <li><b>Mark</b> — ซ่อนรูปในหน้า <code>/PMReport/PM</code> ทันที · <u>ยังไม่ลบไฟล์</u> · rollback ได้ 100%</li>
          <li class="text-danger"><b>Sweep / Purge</b> — <b>ลบไฟล์บน GCS จริง กู้คืนไม่ได้</b> (bucket ไม่มี versioning)</li>
        </ul>
      </div>

      <!-- ================= 🛡️ first-run guard ================= -->
      <div v-if="isFirstRun" class="alert alert-danger">
        <h5 class="mb-2"><i class="fas fa-shield-alt"></i> ระบบนี้ยังไม่เคยทำงาน — นี่คือ <b>first run</b></h5>
        <p class="mb-2">🔴 <b>ห้ามใช้ <code>Purge</code></b> เพราะมันทำ <b>Mark + Sweep ในรอบเดียว</b> ⇒ รูปถูกลบทันทีโดยไม่มีช่วงให้ตรวจสอบ · <b>ลบแล้วกู้ไม่ได้</b></p>
        <div class="pl-3">
          <b>ลำดับที่ถูกต้อง:</b>
          <ol class="mb-0">
            <li><code>Preview</code> → เอาตัวเลขไปทวนกับ user/BA ก่อน</li>
            <li><code>Mark</code> → ตรวจหน้า <code>/PMReport/PM</code> (ยังย้อนกลับได้ 100%)</li>
            <li><b>soak 1–2 สัปดาห์</b> — ปล่อยให้ผู้ใช้ใช้งานจริง 🔴 ตัดขั้นนี้ไม่ได้</li>
            <li><code>Sweep</code> ทีละ batch → จุดที่ย้อนไม่ได้</li>
            <li>เปิด <code>IsEnabled = 1</code> + cron → <code>Purge</code> เริ่มใช้ได้ตั้งแต่ตรงนี้</li>
          </ol>
        </div>
      </div>

      <div v-else-if="soakDays !== null && soakDays < SOAK_MIN_DAYS && pendingCount > 0" class="alert alert-warning">
        <b><i class="fas fa-hourglass-half"></i> ยังไม่ครบ soak period</b> — คิวเก่าสุดถูก mark มา <b>{{ soakDays }}</b> วัน (แนะนำอย่างน้อย {{ SOAK_MIN_DAYS }} วัน)
        <br><small>กด <code>Sweep</code> ได้ถ้าเป็นการทดสอบ batch เล็ก แต่อย่าเพิ่งกวาดทั้งก้อน</small>
      </div>

      <!-- ================= Config ================= -->
      <div class="row">
        <div class="col-xl-7">
          <div class="card mb-3">
            <div class="card-header py-2">
              <b>Config</b> <small class="text-muted">(PMImageRetentionConfig — แก้ได้ด้วย SQL ไม่ต้อง deploy)</small>
            </div>
            <div class="card-body p-0">
              <table class="table table-sm table-bordered mb-0">
                <tbody>
                  <tr>
                    <th class="bg-light" style="width:190px">IsEnabled</th>
                    <td>
                      <span class="badge" :class="config.isEnabled ? 'badge-success' : 'badge-secondary'">
                        {{ config.isEnabled ? "ENABLED" : "DISABLED" }}
                      </span>
                      <small class="text-muted ml-2">
                        มีผลกับ <code>Purge</code> (cron) เท่านั้น — Preview/Mark/Sweep เรียกได้เสมอ
                      </small>
                    </td>
                  </tr>
                  <tr><th class="bg-light">KeepRounds</th><td><b>{{ config.keepRounds }}</b> รอบล่าสุด (ต่อร้าน + เครื่อง)</td></tr>
                  <tr><th class="bg-light">MaxObjectsPerRun</th><td>{{ fmt(config.maxObjectsPerRun) }}</td></tr>
                  <tr><th class="bg-light">DeleteConcurrency</th><td>{{ config.deleteConcurrency }}</td></tr>
                  <tr><th class="bg-light">MachineProductTypeId</th><td>{{ config.machineProductTypeId }} <small class="text-muted">(3 = เครื่องทำความเย็น)</small></td></tr>
                  <tr><th class="bg-light">MaxAgeYears</th><td>{{ config.maxAgeYears == null ? "— (ไม่ใช้)" : config.maxAgeYears }}</td></tr>
                  <tr><th class="bg-light">LogRetentionMonths</th><td>{{ config.logRetentionMonths == null ? "— (เก็บตลอด)" : config.logRetentionMonths }}</td></tr>
                  <tr><th class="bg-light">PathWhitelist</th><td><code>{{ config.pathWhitelist }}</code></td></tr>
                  <tr><th class="bg-light">PathBlacklist</th><td><code>{{ config.pathBlacklist }}</code></td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- ================= คิว ================= -->
        <div class="col-xl-5">
          <div class="card mb-3">
            <div class="card-header py-2"><b>คิวและผลลัพธ์สะสม</b> <small class="text-muted">(PMImagePurgeLog)</small></div>
            <div class="card-body p-0">
              <table class="table table-sm table-bordered mb-0">
                <thead>
                  <tr><th>DeleteStatus</th><th class="text-right" style="width:110px">จำนวน</th></tr>
                </thead>
                <tbody>
                  <tr v-if="queue.length === 0"><td colspan="2" class="text-center text-muted">ยังไม่มีข้อมูล</td></tr>
                  <tr v-for="q in queue" :key="q.status">
                    <td>
                      <span class="badge" :class="queueBadgeClass(q.status)">{{ q.status }}</span>
                      <small class="text-muted ml-1">{{ queueHint(q.status) }}</small>
                    </td>
                    <td class="text-right">{{ fmt(q.count) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="bg-light">
                    <th>รูปที่ถูกซ่อนแล้ว (PMCleanUp)</th>
                    <th class="text-right">{{ fmt(purgedRows) }} / {{ fmt(cloudRows) }}</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div class="card mb-3">
            <div class="card-header py-2"><b>รันล่าสุด</b></div>
            <div class="card-body py-2">
              <div v-if="!lastRun" class="text-muted">ยังไม่เคยรัน</div>
              <div v-else>
                <code>{{ lastJobName }}</code><br />
                {{ formatDateTime(lastRun) }}
                <span class="badge ml-2" :class="lastStatus === 'SUCCESS' ? 'badge-success' : 'badge-danger'">{{ lastStatus }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= 🧪 เครื่องมือทดสอบ ================= -->
      <div class="card mb-3">
        <div class="card-header py-2 d-flex align-items-center" style="cursor:pointer" @click="showTestTools = !showTestTools">
          <b>🧪 เครื่องมือทดสอบ (DEV / SIT)</b>
          <small class="text-muted ml-2">อัปโหลดรูปเข้า GCS เพื่อใช้ seed ข้อมูลทดสอบ</small>
          <i class="fas ml-auto" :class="showTestTools ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </div>
        <div class="card-body" v-show="showTestTools">
          <div class="alert alert-warning py-2 mb-3">
            <b>ใช้ตอน PoC เท่านั้น</b> — ไฟล์จะถูกตั้งชื่อขึ้นต้นด้วย <code>CRTEST_</code> เสมอ เพื่อให้แยกจากรูปงานจริงได้
            <br>path ที่ได้เป็นรูปแบบเดียวกับรูป PM จริง (<code>{{ '{yyyy}' }}/PMJobPicture/{{ '{MM}' }}/…_&lt;guid&gt;.png</code>) จึงผ่าน guard #2 เหมือนกัน
          </div>

          <div class="form-inline mb-2">
            <input type="file" class="form-control-file mr-3" accept=".jpg,.jpeg,.png" multiple ref="testFiles" @change="onPickFiles" />
            <button class="btn btn-sm btn-outline-primary" :disabled="uploading || pickedCount === 0" @click="uploadTestFiles">
              <i v-if="uploading" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-upload"></i>
              {{ uploading ? "กำลังอัปโหลด..." : "อัปโหลด " + pickedCount + " ไฟล์" }}
            </button>
          </div>
          <small class="text-muted">รับ .jpg / .jpeg / .png · ไม่เกิน 5 MB ต่อไฟล์ · แนะนำ 3–6 ใบ</small>

          <div v-if="uploaded.length" class="mt-3">
            <div class="d-flex align-items-center mb-1">
              <b>ผลอัปโหลด ({{ uploaded.length }})</b>
              <button class="btn btn-sm btn-outline-secondary ml-auto" @click="copyPaths">
                <i class="fas fa-copy"></i> คัดลอก path ทั้งหมด
              </button>
            </div>
            <table class="table table-sm table-bordered mb-0">
              <thead><tr><th style="width:40px">#</th><th style="width:90px">ผล</th><th>ObjectName (FileInfoId)</th></tr></thead>
              <tbody>
                <tr v-for="(u, i) in uploaded" :key="i">
                  <td class="text-center">{{ i + 1 }}</td>
                  <td class="text-center">
                    <span class="badge" :class="u.ok ? 'badge-success' : 'badge-danger'">{{ u.ok ? "OK" : "FAIL" }}</span>
                  </td>
                  <td><small><code>{{ u.path || u.message }}</code></small></td>
                </tr>
              </tbody>
            </table>
            <textarea ref="pathBox" class="form-control mt-2" rows="4" readonly :value="pathList"></textarea>
          </div>
        </div>
      </div>

      <!-- ================= Trigger ================= -->
      <div class="card mb-3">
        <div class="card-header py-2"><b>จำลอง Cronjob — Manual Trigger</b></div>
        <div class="card-body">
          <div class="form-inline mb-3">
            <label class="mr-2">MaxObjects (override, ว่าง = ใช้ค่า config):</label>
            <input type="number" class="form-control form-control-sm mr-3" style="width:130px"
                   v-model.number="maxObjects" min="1" placeholder="เช่น 10" />
            <label class="mr-2">SampleSize:</label>
            <input type="number" class="form-control form-control-sm mr-3" style="width:100px"
                   v-model.number="sampleSize" min="1" max="500" />
            <label class="kt-checkbox kt-checkbox--brand mb-0">
              <input type="checkbox" v-model="persist" /> เขียนแถว DRYRUN ลง log (Preview)
              <span></span>
            </label>
          </div>

          <div class="row">
            <div class="col-xl-3 col-md-6 mb-2" v-for="a in actions" :key="a.mode">
              <div class="trigger-card" :class="a.tone">
                <div class="tc-head">
                  <i :class="a.icon"></i>
                  <span class="tc-name">{{ a.title }}</span>
                  <span class="badge badge-light ml-auto">{{ a.phase }}</span>
                </div>
                <p class="tc-desc">{{ a.desc }}</p>
                <div class="tc-risk" :class="a.tone">{{ a.risk }}</div>
                <div v-if="isBlocked(a)" class="tc-blocked">
                  🔒 ปิดไว้เพราะเป็น first run — ใช้ <b>Mark</b> ก่อน
                </div>
                <button class="btn btn-sm btn-block" :class="a.btn"
                        :disabled="running !== null || isBlocked(a)"
                        @click="trigger(a)">
                  <i v-if="running === a.mode" class="fas fa-spinner fa-spin"></i>
                  <i v-else class="fas fa-play"></i>
                  {{ running === a.mode ? "กำลังรัน..." : "เรียก " + a.endpoint }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= ผลลัพธ์ ================= -->
      <div v-if="result" class="card mb-3">
        <div class="card-header py-2 d-flex align-items-center">
          <b>ผลลัพธ์ — {{ result.mode }}</b>
          <span class="badge ml-2" :class="resultOk ? 'badge-success' : 'badge-danger'">
            {{ resultOk ? "SUCCESS" : "FAILED" }}
          </span>
          <span v-if="result.skipped" class="badge badge-warning ml-2">SKIPPED</span>
          <button class="btn btn-sm btn-outline-secondary ml-auto" @click="showRaw = !showRaw">
            {{ showRaw ? "ซ่อน" : "ดู" }} JSON
          </button>
        </div>
        <div class="card-body">
          <div v-if="errorMessage" class="alert alert-danger mb-3">{{ errorMessage }}</div>
          <div v-if="result.skipReason" class="alert alert-warning mb-3">{{ result.skipReason }}</div>

          <table class="table table-sm table-bordered">
            <tbody>
              <tr>
                <th class="bg-light">BatchId</th><td colspan="3"><code>{{ result.batchId }}</code></td>
              </tr>
              <tr>
                <th class="bg-light" style="width:180px">Candidate</th><td>{{ fmt(result.candidateCount) }}</td>
                <th class="bg-light" style="width:180px">Marked</th><td>{{ fmt(result.markedCount) }}</td>
              </tr>
              <tr>
                <th class="bg-light">Deleted</th><td class="text-danger"><b>{{ fmt(result.deletedCount) }}</b></td>
                <th class="bg-light">NotFound</th><td>{{ fmt(result.notFoundCount) }}</td>
              </tr>
              <tr>
                <th class="bg-light">Failed</th><td>{{ fmt(result.failedCount) }}</td>
                <th class="bg-light">Pending คงเหลือ</th><td>{{ fmt(result.pendingRemaining) }}</td>
              </tr>
              <tr>
                <th class="bg-light">Skipped (shared)</th><td>{{ fmt(result.skippedSharedCount) }}</td>
                <th class="bg-light">Skipped (path)</th><td>{{ fmt(result.skippedPathCount) }}</td>
              </tr>
              <tr>
                <th class="bg-light">ไม่มี barcode เครื่อง</th><td>{{ fmt(result.noBarcodeImageCount) }}</td>
                <th class="bg-light">Duration</th><td>{{ fmt(result.durationMs) }} ms</td>
              </tr>
            </tbody>
          </table>

          <div v-if="result.samples && result.samples.length" class="table-responsive">
            <h6 class="mt-3">ตัวอย่างรูปที่เข้าเงื่อนไข ({{ result.samples.length }} แถวแรก)</h6>
            <table class="table table-sm table-striped table-bordered">
              <thead>
                <tr>
                  <th class="text-center">#</th>
                  <th class="text-center">CleanUpId</th>
                  <th class="text-center">ShopId</th>
                  <th class="text-center">SubAgent</th>
                  <th class="text-center">BarcodeId</th>
                  <th class="text-center">Rank</th>
                  <th class="text-center">วันที่ล้าง</th>
                  <th class="text-center">ประเภทรูป</th>
                  <th>ObjectName</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(s, i) in result.samples" :key="s.cleanUpId">
                  <td class="text-center">{{ i + 1 }}</td>
                  <td class="text-center">{{ s.cleanUpId }}</td>
                  <td class="text-center">{{ s.shopId }}</td>
                  <td class="text-center">{{ s.subAgentShopId || "-" }}</td>
                  <td class="text-center">{{ s.barcodeId }}</td>
                  <td class="text-center"><span class="badge badge-dark">{{ s.roundRank }}</span></td>
                  <td class="text-center">{{ formatDate(s.roundDate) }}</td>
                  <td class="text-center"><small>{{ s.cleanUpChoiceCode }}</small></td>
                  <td><small><code>{{ s.objectName }}</code></small></td>
                </tr>
              </tbody>
            </table>
          </div>

          <pre v-if="showRaw" class="raw-json">{{ rawJson }}</pre>
        </div>
      </div>

      <!-- ================= confirm modal ================= -->
      <div v-if="pending" class="modal-overlay" @click.self="pending = null">
        <div class="modal-box confirm-box">
          <div class="modal-header">
            <h5 class="text-danger mb-0"><i class="fas fa-exclamation-triangle"></i> ยืนยันการรัน {{ pending.title }}</h5>
            <button type="button" class="close" @click="pending = null">&times;</button>
          </div>
          <div class="modal-body">
            <div v-if="isBlocked(pending)" class="alert alert-danger">
              <b>🔒 ถูกบล็อก — ระบบนี้ยังไม่เคยทำงาน (first run)</b><br>
              <code>Purge</code> ทำ Mark + Sweep ในรอบเดียว ⇒ รูปถูกลบทันทีโดยไม่มีช่วงให้ตรวจสอบ<br>
              ให้ใช้ <b>Mark</b> → soak 1–2 สัปดาห์ → <b>Sweep</b> แทน
            </div>
            <p>{{ pending.confirmText }}</p>
            <div v-if="pending.needPhrase" class="alert alert-danger">
              การกระทำนี้ <b>ลบไฟล์บน GCS จริงและกู้คืนไม่ได้</b><br />
              พิมพ์ <code>{{ CONFIRM_PHRASE }}</code> เพื่อยืนยัน
              <input type="text" class="form-control mt-2" v-model="confirmInput" :placeholder="CONFIRM_PHRASE" />
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="pending = null">ยกเลิก</button>
            <button class="btn btn-danger"
                    :disabled="isBlocked(pending) || (pending.needPhrase && confirmInput !== CONFIRM_PHRASE)"
                    @click="run(pending)">
              ยืนยันรัน
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      CONFIRM_PHRASE: "DELETE",
      SOAK_MIN_DAYS: 7,
      isFirstRun: false,
      soakDays: null,
      config: {},
      queue: [],
      purgedRows: 0,
      cloudRows: 0,
      lastRun: null,
      lastStatus: null,
      lastJobName: null,

      maxObjects: null,
      sampleSize: 20,
      persist: false,

      isLoading: false,
      running: null,
      result: null,
      errorMessage: null,
      rawJson: "",
      showRaw: false,

      showTestTools: false,
      uploading: false,
      pickedCount: 0,
      uploaded: [],

      pending: null,
      confirmInput: "",

      actions: [
        {
          mode: "PREVIEW", endpoint: "Preview", title: "Preview", phase: "Phase 2",
          icon: "fas fa-search text-primary", tone: "safe", btn: "btn-primary",
          desc: "นับรูปที่เข้าเงื่อนไข + ดูตัวอย่าง ไม่แตะข้อมูลใด ๆ",
          risk: "ปลอดภัย 100% — ไม่เขียนอะไรเลย",
          confirm: false,
        },
        {
          mode: "MARK", endpoint: "Mark", title: "Mark", phase: "Phase 3",
          icon: "fas fa-eye-slash text-warning", tone: "warn", btn: "btn-warning",
          desc: "เข้าคิว + ซ่อนรูปในหน้า /PMReport/PM ทันที",
          risk: "ยังไม่ลบไฟล์ — rollback ได้ 100%",
          confirm: true, needPhrase: false,
          confirmText: "รูปจะหายจากหน้าจอทันที แต่ไฟล์บน GCS ยังอยู่ครบ · ย้อนกลับได้ตามขั้นตอน §6.11",
        },
        {
          mode: "SWEEP", endpoint: "Sweep", title: "Sweep", phase: "Phase 4",
          icon: "fas fa-trash text-danger", tone: "danger", btn: "btn-danger",
          desc: "ลบไฟล์บน GCS ตามคิว PENDING / FAILED",
          risk: "🔴 ลบจริง กู้ไม่ได้",
          confirm: true, needPhrase: true,
          confirmText: "จะลบไฟล์บน GCS ตามคิวที่ค้างอยู่ (จำกัดด้วย MaxObjects)",
        },
        {
          mode: "PURGE", endpoint: "Purge", title: "Purge (cron)", phase: "Phase 5",
          icon: "fas fa-clock text-danger", tone: "danger", btn: "btn-danger",
          desc: "เส้นเดียวกับที่ cron เรียก: Mark → Sweep → Housekeep",
          risk: "🔴 ลบจริง · เคารพ IsEnabled",
          confirm: true, needPhrase: true,
          confirmText: "จำลองการทำงานของ cronjob 02:00 ทั้งกระบวนการ — ถ้า IsEnabled = 0 จะ return ทันทีโดยไม่ทำอะไร",
        },
      ],
    };
  },
  computed: {
    resultOk() {
      return this.errorMessage == null;
    },
    pendingCount() {
      var q = this.queue.filter(function (x) { return x.status === "PENDING" || x.status === "FAILED"; });
      return q.reduce(function (a, b) { return a + (b.count || 0); }, 0);
    },
    pathList() {
      return this.uploaded.filter(function (u) { return u.ok; })
                          .map(function (u) { return u.path; })
                          .join(String.fromCharCode(10));
    },
  },
  mounted() {
    this.loadStatus();
  },
  methods: {
    onPickFiles() {
      var f = this.$refs.testFiles;
      this.pickedCount = f && f.files ? f.files.length : 0;
    },
    uploadTestFiles() {
      var self = this;
      var input = self.$refs.testFiles;
      if (!input || !input.files || input.files.length === 0) return;
      var files = Array.prototype.slice.call(input.files);
      self.uploading = true;
      self.uploaded = [];

      // อัปโหลดทีละไฟล์ตามลำดับ — จำนวนน้อย ไม่ต้องขนาน
      var chain = Promise.resolve();
      files.forEach(function (f) {
        chain = chain.then(function () {
          var fd = new FormData();
          fd.append("file", f);
          return axios.post("/Api/PMImageRetention/TestUpload", fd, {
            headers: { "Content-Type": "multipart/form-data" }
          }).then(function (res) {
            var d = res.data || {};
            self.uploaded.push({
              ok: d.status === "SUCCESS",
              path: d.fileInfoId,
              message: d.message
            });
          }).catch(function (e) {
            self.uploaded.push({
              ok: false,
              message: (e.response && e.response.data && e.response.data.message) || e.message
            });
          });
        });
      });

      chain.finally(function () {
        self.uploading = false;
        self.pickedCount = 0;
        if (input) input.value = "";
      });
    },
    copyPaths() {
      var box = this.$refs.pathBox;
      if (!box) return;
      box.select();
      try { document.execCommand("copy"); } catch (e) { /* เบราว์เซอร์ไม่รองรับ → ให้ copy เอง */ }
    },
    loadStatus() {
      var self = this;
      self.isLoading = true;
      axios
        .get("/Api/PMImageRetention/Status")
        .then(function (res) {
          var d = res.data || {};
          self.config = d.config || {};
          self.queue = d.queue || [];
          self.purgedRows = d.purgedRows || 0;
          self.cloudRows = d.cloudRows || 0;
          self.lastRun = d.lastRun || null;
          self.lastStatus = d.lastStatus || null;
          self.lastJobName = d.lastJobName || null;
          self.isFirstRun = !!d.isFirstRun;
          self.soakDays = (d.soakDays === undefined ? null : d.soakDays);
        })
        .catch(function (e) {
          console.error("load status fail", e);
        })
        .finally(function () {
          self.isLoading = false;
        });
    },
    // 🛡️ first-run guard — Purge ทำ Mark+Sweep รอบเดียว ห้ามใช้ตอนที่ยังไม่เคย mark อะไรเลย
    isBlocked(action) {
      if (!action) return false;
      return action.mode === "PURGE" && this.isFirstRun;
    },
    trigger(action) {
      if (this.isBlocked(action)) return;
      if (!action.confirm) {
        this.run(action);
        return;
      }
      this.confirmInput = "";
      this.pending = action;
    },
    run(action) {
      var self = this;
      self.pending = null;
      self.running = action.mode;
      self.result = null;
      self.errorMessage = null;

      var params = {};
      if (self.maxObjects) params.maxObjects = self.maxObjects;
      if (action.mode === "PREVIEW") {
        params.persist = self.persist;
        params.sampleSize = self.sampleSize;
      }

      axios
        .get("/Api/PMImageRetention/" + action.endpoint, { params: params })
        .then(function (res) {
          var d = res.data || {};
          self.rawJson = JSON.stringify(d, null, 2);
          if (d.status === "FAILED") {
            self.errorMessage = d.message || "รันไม่สำเร็จ";
            self.result = { mode: action.mode, batchId: d.batchId };
          } else {
            self.result = d.result || { mode: action.mode };
          }
          self.loadStatus();
        })
        .catch(function (e) {
          self.errorMessage = (e.response && e.response.data && e.response.data.message) || e.message;
          self.result = { mode: action.mode };
          self.rawJson = JSON.stringify(e.response ? e.response.data : e.message, null, 2);
        })
        .finally(function () {
          self.running = null;
        });
    },
    queueBadgeClass(s) {
      if (s === "PENDING") return "badge-warning";
      if (s === "DELETED") return "badge-danger";
      if (s === "NOTFOUND") return "badge-secondary";
      if (s === "FAILED") return "badge-dark";
      if (s === "REVERTED") return "badge-info";
      if (s === "DRYRUN") return "badge-light";
      return "badge-success";
    },
    queueHint(s) {
      if (s === "PENDING") return "รอลบ";
      if (s === "DELETED") return "ลบแล้ว";
      if (s === "NOTFOUND") return "ไม่พบไฟล์ (ถือว่าสำเร็จ)";
      if (s === "FAILED") return "รอ retry";
      if (s === "SKIPPED_SHARED") return "มีคนอ้างอยู่ ไม่ลบ";
      if (s === "SKIPPED_PATH") return "path ไม่ผ่าน guard";
      if (s === "REVERTED") return "ยกเลิกถาวร";
      if (s === "DRYRUN") return "แถวจำลอง";
      return "";
    },
    fmt(n) {
      if (n == null) return "-";
      return Number(n).toLocaleString();
    },
    formatDateTime(v) {
      if (!v) return "-";
      var d = new Date(v);
      return d.toLocaleDateString("th-TH") + " " + d.toLocaleTimeString("th-TH");
    },
    formatDate(v) {
      if (!v) return "-";
      return new Date(v).toLocaleDateString("th-TH");
    },
  },
};
</script>

<style scoped>
.trigger-card {
  border: 1px solid #ebedf2;
  border-radius: 6px;
  padding: 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.trigger-card.safe { border-color: #b3d4ff; background: #f5faff; }
.trigger-card.warn { border-color: #ffe0a3; background: #fffaf0; }
.trigger-card.danger { border-color: #ffc2c2; background: #fff5f5; }
.tc-head {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-bottom: 6px;
}
.tc-name { margin-left: 8px; }
.tc-desc {
  font-size: 0.85rem;
  color: #74788d;
  min-height: 38px;
  margin-bottom: 6px;
}
.tc-risk {
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 10px;
}
.tc-risk.safe { color: #0b7285; }
.tc-risk.warn { color: #b7791f; }
.tc-risk.danger { color: #c53030; }
.tc-blocked {
  background: #fff5f5;
  border: 1px dashed #fc8181;
  color: #c53030;
  font-size: 0.78rem;
  border-radius: 4px;
  padding: 5px 8px;
  margin-bottom: 8px;
}
.raw-json {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 12px;
  border-radius: 4px;
  font-size: 12px;
  max-height: 420px;
  overflow: auto;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.modal-box {
  background: white;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
}
.confirm-box { width: 90vw; max-width: 560px; }
.modal-header {
  padding: 12px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.modal-body { padding: 20px; }
.modal-footer {
  padding: 12px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}
.modal-footer .btn + .btn { margin-left: 8px; }
</style>



// WEBPACK FOOTER //
// App/Components/Monitoring/MonitoringClearImage.vue